import { useCallback, useEffect, useRef, useState } from "react";
import { extractErrorMessage } from "@/api/client";
import { useRegal, useUlozRegal, vetaVlozenia } from "@/api/hooks/useRegal";
import type { RegalUlozRequest } from "@/api/schemas";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";

/** Správy stránky Regál CRC (`tools/crc_cennik/regal/stranka.js`, KOLO 232). */
type SpravaStranky =
  | { typ: "regal-crc:pripravena"; verzia: number }
  | { typ: "regal-crc:zmena"; zmeneny: boolean; zostav: number }
  | ({ typ: "regal-crc:vlozit" } & RegalUlozRequest)
  | { typ: "regal-crc:zavriet"; zmeneny: boolean };

const ADRESA = "/regal/?vlozene=1";

/**
 * Konfigurátor regálu CRC v okne cez celú obrazovku (KOLO 232). User: *„dnešná
 * stránka v okne“* — stránka Regál CRC (jeden zdroj s artifactom) beží v iframe
 * rovnakého pôvodu a hovorí cez `postMessage`: Rozpočet jej dá regál rozpočtu
 * tovaru, Vložiť do rozpočtu pošle zostavy a diely Tandemu (`PUT /regal`).
 * Rozpočtár (tovar len na čítanie) regál len pozrie.
 */
export function RegalDialog({
  zakazkaId,
  variantaId,
  uid,
  lenCitanie,
  onOpenChange,
}: {
  zakazkaId: string;
  variantaId: string;
  /** Upraviť regál z pásu zostavy — stránka otvorí túto zostavu. */
  uid?: string | null;
  lenCitanie: boolean;
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const ram = useRef<HTMLIFrameElement>(null);
  const stav = useRegal(variantaId);
  const uloz = useUlozRegal(zakazkaId, variantaId);
  const [zmeneny, setZmeneny] = useState(false);
  const [pytaj, setPytaj] = useState(false);

  const posli = useCallback((sprava: Record<string, unknown>) => {
    ram.current?.contentWindow?.postMessage(sprava, window.location.origin);
  }, []);

  useEffect(() => {
    function na(e: MessageEvent<SpravaStranky>): void {
      if (e.origin !== window.location.origin || e.source !== ram.current?.contentWindow) return;
      const sprava = e.data;
      if (!sprava || typeof sprava !== "object" || !("typ" in sprava)) return;
      if (sprava.typ === "regal-crc:pripravena") {
        if (!stav.data) return; // odpovie efekt nižšie, keď sa stav načíta
        posli({
          typ: "regal-crc:stav",
          regal: stav.data.regal ?? null,
          zakazka: stav.data.zakazka,
          prefix: stav.data.prefix,
          uid: uid ?? null,
          iba_citanie: lenCitanie,
        });
      } else if (sprava.typ === "regal-crc:zmena") {
        setZmeneny(sprava.zmeneny);
      } else if (sprava.typ === "regal-crc:zavriet") {
        if (sprava.zmeneny) setPytaj(true);
        else onOpenChange(false);
      } else if (sprava.typ === "regal-crc:vlozit") {
        if (lenCitanie) return;
        const { typ: _typ, ...telo } = sprava;
        uloz.mutate(telo as RegalUlozRequest, {
          onSuccess: (v) => {
            const veta = vetaVlozenia(v);
            posli({ typ: "regal-crc:vlozene", ok: true, veta });
            setZmeneny(false);
            toast({ title: "Regál CRC", description: veta });
          },
          onError: (err) => {
            const veta = extractErrorMessage(err);
            posli({ typ: "regal-crc:vlozene", ok: false, veta });
            toast({ variant: "destructive", title: "Regál sa nevložil", description: veta });
          },
        });
      }
    }
    window.addEventListener("message", na);
    return () => window.removeEventListener("message", na);
  }, [stav.data, uid, lenCitanie, onOpenChange, posli, uloz]);

  // stránka mohla poslať „pripravená“ skôr, než prišiel stav — pošli ho, keď príde
  const poslany = useRef(false);
  useEffect(() => {
    if (!stav.data || poslany.current) return;
    poslany.current = true;
    posli({
      typ: "regal-crc:stav",
      regal: stav.data.regal ?? null,
      zakazka: stav.data.zakazka,
      prefix: stav.data.prefix,
      uid: uid ?? null,
      iba_citanie: lenCitanie,
    });
  }, [stav.data, uid, lenCitanie, posli]);

  return (
    <>
      <Dialog
        open
        onOpenChange={(o) => {
          if (o) return;
          if (zmeneny && !lenCitanie) setPytaj(true);
          else onOpenChange(false);
        }}
      >
        <DialogContent className="h-[calc(100dvh-1rem)] max-h-none w-[calc(100vw-1rem)] max-w-none gap-0 overflow-hidden p-0 [&>button.absolute]:hidden">
          <DialogTitle className="sr-only">Konfigurátor regálu CRC</DialogTitle>
          <DialogDescription className="sr-only">
            {lenCitanie
              ? "Regál rozpočtu tovaru na čítanie."
              : "Zostavy regálu sa do rozpočtu tovaru vložia tlačidlom Vložiť do rozpočtu."}
          </DialogDescription>
          {stav.isError ? (
            <p className="p-6 text-sm text-destructive">
              Regál sa nenačítal: {extractErrorMessage(stav.error)}
            </p>
          ) : (
            <iframe
              ref={ram}
              src={ADRESA}
              title="Konfigurátor regálu CRC"
              className="h-full w-full border-0"
              data-testid="regal-ram"
            />
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={pytaj} onOpenChange={setPytaj}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Zavrieť konfigurátor?</DialogTitle>
            <DialogDescription>
              Regál má zmeny, ktoré nie sú v rozpočte. Bez Vložiť do rozpočtu sa stratia.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPytaj(false)}>
              Späť do regálu
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setPytaj(false);
                onOpenChange(false);
              }}
            >
              Zavrieť bez vloženia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
